import { z } from 'zod';
import { SUPPORTED_LOCALES } from '../constants';

// =============================================================================
// Templates gallery — public, ungated hub templates (gym, clinic, …) distributed
// two ways from one private authoring source (`wayai-templates/`): functionally via
// the CLI (`wayai template pull <slug> [--lang <locale>]` → files → `wayai push`) and
// for discovery via `wayai.pro/templates`. WayAI's store holds the WAYAI SLICE +
// joint metadata only; the Rekor backend each channel runs on lives in Rekor's own
// store (cross-product boundary).
//
// CHANNEL→BACKEND EDGE (gh #2955): the real topology is N:1 — several WayAI channels
// sit on one Rekor backend (`gym-leads-evo` + `gym-members-evo` → `gym-evo`), with a
// rare N:N exception. A WayAI channel template declares its backend(s) with a typed
// `uses: [{ backend, endpoint }]` list; the cross-link URL + Rekor MCP Factory
// endpoint are DERIVED from those slugs (`deriveRekorBackendUrl` /
// `deriveRekorMcpEndpoint`), never hand-authored. WayAI owns the edge; Rekor derives
// the reverse ("channels on this backend") at its own gallery build.
//
// LOCALIZATION: one slug, per-locale content. The localized facets (`source`,
// `metaTitle`, `metaDescription`, README) are authored per locale; locale-agnostic
// facets stay flat: `installCommand`, the `uses` edge, and hero. The WEBSITE omits
// untranslated locales; the CLI falls back to `defaultLocale`.
// =============================================================================

// Templates can only declare a locale the marketing site can route.
export const templateLocaleSchema = z.enum(SUPPORTED_LOCALES);
export type TemplateLocale = z.infer<typeof templateLocaleSchema>;

// A slug is a public URL segment + R2 key prefix + DO primary key — constrain it.
export const TEMPLATE_SLUG_REGEX = /^[a-z0-9][a-z0-9-]{0,62}$/;
export const templateSlugSchema = z
  .string()
  .regex(TEMPLATE_SLUG_REGEX, 'slug must be lowercase alphanumeric with hyphens (max 63)');

// `published` is the only status that reaches the store; `draft`/`internal` stay
// version-controlled in `tutorials/` but are unpublished by the reconcile.
export const templateStatusSchema = z.enum(['published', 'draft', 'internal']);
export type TemplateStatus = z.infer<typeof templateStatusSchema>;

// File paths become R2 keys (`templates/<slug>/<locale>/wayai/<path>`) AND on-disk
// write targets in the consumer's workspace — both are path-traversal sinks.
export function isSafeTemplatePath(path: string): boolean {
  if (path.length === 0 || path.length > 300) return false;
  if (path.startsWith('/') || path.includes('\\')) return false;
  const segments = path.split('/');
  return segments.every(
    (seg) => seg.length > 0 && seg !== '.' && seg !== '..' && /^[A-Za-z0-9._-]+$/.test(seg),
  );
}

const templateFileMapSchema = z
  .record(z.string())
  .refine((m) => Object.keys(m).every(isSafeTemplatePath), {
    message: 'file map contains an unsafe path (no absolute paths, "..", or backslashes)',
  });

// -----------------------------------------------------------------------------
// Slice sanitization — strip INSTANCE identifiers from a template's wayai config
// slice at publish time (gh #2939). A published template is a portable BLUEPRINT:
// the consumer's `wayai template pull <slug> && wayai push` must create a NEW hub
// in THEIR org, not reference the author's. The source slice (produced by the
// author's `wayai pull`) carries the author hub's instance ids: top-level
// `hub_id`/`hub_environment` (which misroute `wayai push` to the author's hub) and
// per-entity UUIDs (`connection_id`/`target_id`/`agent_id`/…) that don't exist in
// the consumer's org. Each id has a portable NAME twin alongside it (`connection`,
// `target`, `agent`, `list`, `template_whatsapp`) — the names are what re-resolve
// on push — so the UUIDs are redundant instance cruft AND leak author-org internals
// into the public gallery.
//
// The strip recurses anywhere EXCEPT user-defined opaque blobs (JSON Schemas,
// connector settings, tool configs, eval/journey fixtures), which can legitimately
// contain a property literally named `id`/`connection_id`. The opaque-key stoplist
// is the guard. Keep both lists in sync with the HubAsCode contract in `ci.ts`: a
// new entity field that carries an instance id → add its key here; a new opaque
// blob field → add it to the stoplist.
//
// DELIBERATELY KEPT (required portable-blueprint content, NOT instance cruft):
// connection `base_url` (the connector endpoint the consumer needs) and `credential`
// (the org-credential display name re-bound by name on push). Both are author-chosen
// but load-bearing for the template to stand up; an author who publishes an internal
// hostname in `base_url` owns that, same as any authored field. This differs from
// author-org data with NO consumer value (outbound-contact phone/email/sid, the
// `account_sid` channel identifier) — a SEPARATE concern handled by the author-data
// EXCLUSION below (gh #2946), not by this instance-id strip.
// -----------------------------------------------------------------------------

/** Object keys that hold an INSTANCE identifier — dropped from the published slice. */
export const TEMPLATE_INSTANCE_ID_KEYS: ReadonlySet<string> = new Set([
  // Top-level hub instance metadata (hub.yaml) — set by `wayai pull`, never authored.
  'hub_id',
  'hub_environment',
  'preview_label',
  'branch_name',
  // Per-entity row id + cross-entity reference UUIDs (each has a portable name twin).
  'id',
  'connection_id',
  'target_id',
  'agent_id',
  'resource_id',
  'list_id',
  // Account-scoped Meta WhatsApp template id (name twin: `template_whatsapp`).
  'template_whatsapp_id',
]);

/** Keys whose VALUE is a user-defined opaque blob — kept verbatim, never recursed
 *  into (a property literally named `id` inside one is user data, not an instance id). */
export const TEMPLATE_OPAQUE_BLOB_KEYS: ReadonlySet<string> = new Set([
  'settings', // agent + connection connector settings
  'schema_json', // agent response_format JSON Schema
  'json_schema', // state JSON Schema
  'initial_value', // state seed value
  'additional_context_schema', // kanban-status transition-form schema
  'config', // custom-tool OpenAI function schema
  'headers',
  'query_params',
  'body_params', // custom-tool HTTP shaping
  'mappings', // composed-tool argument mappings
  'input',
  'expected',
  'history', // eval message fixtures (tool_calls carry their own `id`)
  'transcript',
  'step_config', // journey fixtures (transcript carries `step_id`; step_config is keyed by it)
]);

// -----------------------------------------------------------------------------
// Author-data EXCLUSION — drop author-org data that has NO consumer value from the
// published slice (gh #2946). DISTINCT from the instance-id strip above (gh #2939):
// that one removes redundant id-shaped keys for PORTABILITY; this one removes whole
// entities/fields for PRIVACY. A published template is a portable BLUEPRINT — it must
// not carry the author's real lead list or account-scoped channel identifiers.
//
// The excluded keys carry VALUES, not portable name twins, so they can't ride the
// instance-id denylist (and `name` — load-bearing for the strip's ref re-resolution —
// can't be denylisted at all, which is why a contact whose required `name` IS PII
// forces dropping the whole entity rather than scrubbing a field). Kept as a separate
// set + function on purpose, not merged into TEMPLATE_INSTANCE_ID_KEYS.
// -----------------------------------------------------------------------------

/** Keys holding author-org data with no portable-blueprint value — dropped wholesale. */
export const TEMPLATE_AUTHOR_DATA_KEYS: ReadonlySet<string> = new Set([
  // Outbound campaign subsystem (hub.yaml top-level). `outbound_contacts` is the
  // author's real leads — `phone`/`email`/`instagram_sid` + the required `name` are
  // GDPR-grade PII. `outbound_lists` group those contacts by real name. `outbound_schedules`
  // bind to the (now-absent) lists and carry the author's campaign cadence + message
  // copy — operationally tied to the author's customers; a consumer builds their own.
  'outbound_contacts',
  'outbound_lists',
  'outbound_schedules',
  // Account-scoped channel identifier on a connection (Twilio SID / WhatsApp number /
  // IG username / from-email, per connector). The connection stays as portable
  // structure re-bound by name on push; this author-account value carries no blueprint
  // value. A dedicated top-level column — never nested in the `settings` opaque blob.
  'account_sid',
]);

/**
 * Recursively drop keys in `drop` from a parsed slice object. Returns a NEW value
 * (does not mutate input). Opaque user blobs (`TEMPLATE_OPAQUE_BLOB_KEYS`) are passed
 * through untouched — a property literally named like a dropped key inside one is user
 * data, not the structured field we mean to remove.
 */
function dropTemplateKeys(value: unknown, drop: ReadonlySet<string>): unknown {
  if (Array.isArray(value)) return value.map((v) => dropTemplateKeys(v, drop));
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      if (drop.has(key)) continue;
      out[key] = TEMPLATE_OPAQUE_BLOB_KEYS.has(key) ? child : dropTemplateKeys(child, drop);
    }
    return out;
  }
  // Non-plain objects (a `Date`/`Buffer` from js-yaml's default schema, etc.) are
  // leaves — recursing would rebuild them via `Object.entries()` and silently
  // destroy them (a `Date` → `{}`). Return verbatim.
  return value;
}

/**
 * Recursively drop instance-identifier keys from a parsed slice object, leaving
 * portable name references intact. Returns a NEW value (does not mutate input).
 */
export function stripTemplateInstanceIds(value: unknown): unknown {
  return dropTemplateKeys(value, TEMPLATE_INSTANCE_ID_KEYS);
}

/**
 * Recursively drop author-org data with no portable-blueprint value (outbound
 * contacts/lists/schedules, account-scoped channel identifiers) from a parsed slice
 * object (gh #2946). Returns a NEW value (does not mutate input).
 */
export function excludeTemplateAuthorData(value: unknown): unknown {
  return dropTemplateKeys(value, TEMPLATE_AUTHOR_DATA_KEYS);
}

/** A YAML-decoded plain object — NOT a `Date`/`Buffer`/`RegExp` — safe to recurse + rebuild. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Is this slice path a hub-config YAML file (hub.yaml / agents / evals / journeys)
 * that should be id-sanitized? Everything under `resources/` is arbitrary user
 * content — a knowledge doc may legitimately be YAML with `id:` keys — and is left
 * untouched; non-YAML files (.md instructions) carry no ids. The check is a
 * denylist of `resources/` rather than an allowlist of config dirs so a future
 * config-entity dir is sanitized by default (fails toward fixing the bug class,
 * not re-leaking).
 */
export function isSanitizableSliceFile(path: string): boolean {
  if (!/\.ya?ml$/i.test(path)) return false;
  return path !== 'resources' && !path.startsWith('resources/');
}

// Localized SEO text shared by the wire + store (camelCase to match `template.yaml`).
const templateTextSchema = z.object({
  metaTitle: z.string().min(1).max(200),
  metaDescription: z.string().max(1000).default(''),
});
export type TemplateText = z.infer<typeof templateTextSchema>;

// -----------------------------------------------------------------------------
// Channel→backend edge (`uses`) + cross-product URL derivation (gh #2955).
// A WayAI channel template declares the Rekor backend(s) it runs on as a typed list
// of `{ backend, endpoint }` slug pairs (normally one entry; N:N = more). The
// cross-link to Rekor's gallery and the Rekor MCP Factory endpoint are DERIVED from
// those slugs — never hand-authored — so there is one source of truth (the edge) and
// no URL drift. WayAI stores only the edge; Rekor builds the reverse ("channels on
// this backend") at its own gallery build.
// -----------------------------------------------------------------------------

// Rekor (sibling product) public bases. The WayAI side derives every Rekor URL from a
// `uses` edge's slugs against these, rather than hand-authoring per-template URLs.
export const REKOR_GALLERY_BASE_URL = 'https://rekor.pro';
export const REKOR_MCP_FACTORY_BASE_URL = 'https://mcp.rekor.pro';

/** The Rekor gallery cross-link for a backend slug (`runs on backend X →`). */
export function deriveRekorBackendUrl(backend: string): string {
  return `${REKOR_GALLERY_BASE_URL}/templates/${backend}`;
}

/** The Rekor MCP Factory endpoint a channel binds to, derived from its endpoint slug. */
export function deriveRekorMcpEndpoint(endpoint: string): string {
  return `${REKOR_MCP_FACTORY_BASE_URL}/e/${endpoint}/mcp`;
}

// One channel→backend dependency. `backend` is the Rekor backend template slug (the
// page the cross-link points at, e.g. `gym-evo`); `endpoint` is the Rekor endpoint /
// hub this channel binds to (e.g. `gym-leads-evo`) — the MCP Factory endpoint is
// derived from it. Both explicit (not endpoint-only) so the cross-link backend slug
// is never re-derived. Locale-agnostic.
export const templateUsesEntrySchema = z.object({
  backend: templateSlugSchema,
  endpoint: templateSlugSchema,
});
export type TemplateUsesEntry = z.infer<typeof templateUsesEntrySchema>;

// The `uses` list. Normally one entry; an empty list means a standalone WayAI channel
// (no Rekor backend). Capped to bound the cross-link render + content hash.
export const templateUsesSchema = z.array(templateUsesEntrySchema).max(24).default([]);

/**
 * Distinct backend slugs a `uses` edge references, in first-seen order — one cross-link
 * card / CLI hint per backend even when an N:1 (or N:N) channel lists a backend under
 * several endpoints. Shared by the website detail page + the consumer CLI so the
 * "backends this channel runs on" rule has a single definition.
 *
 * Tolerates a MISSING `uses` (→ `[]`): the website + CLI are eventually-consistent and
 * may read a catalog served by a backend that predates `uses`, where the field is absent
 * on the wire — same cross-version resilience the gallery already applies to `tags`.
 */
export function distinctUsesBackends(uses: TemplateUsesEntry[] | undefined): string[] {
  return [...new Set((uses ?? []).map((u) => u.backend))];
}

// -----------------------------------------------------------------------------
// `template.yaml` — authoring manifest (CLI reads local files against this).
// `source` is the authoring-only pointer to the native workspace slice (one per
// locale); stripped before the wire push (the store holds files, not the pointer).
// -----------------------------------------------------------------------------
const templateYamlLocaleEntry = templateTextSchema.extend({ source: z.string().min(1) });

export const templateYamlWayaiBlock = z.object({
  installCommand: z.string().min(1).max(300),
  locales: z.record(templateLocaleSchema, templateYamlLocaleEntry),
});

// Cross-cutting filter tags for the website gallery (e.g. `whatsapp`, `lead-gen`,
// `sales`). Normalized — trimmed, lower-cased, de-duped — so filtering is stable
// regardless of author casing/order; `category` stays the single primary taxonomy.
export const templateTagsSchema = z
  .array(z.string().min(1).max(30))
  .max(12)
  .default([])
  .transform((tags) => [...new Set(tags.map((t) => t.trim().toLowerCase()).filter(Boolean))]);

export const templateYamlSchema = z
  .object({
    slug: templateSlugSchema,
    category: z.string().min(1).max(60),
    tags: templateTagsSchema,
    status: templateStatusSchema.default('draft'),
    order: z.number().int().min(0).max(100000).optional(),
    defaultLocale: templateLocaleSchema,
    hero: z.string().optional(), // relative path to a hero image inside the template folder
    // The Rekor backend(s) this channel runs on (gh #2955). Locale-agnostic. The
    // cross-link + MCP endpoint are derived from each entry's slugs, never authored.
    uses: templateUsesSchema,
    // A WayAI template IS a WayAI channel — the wayai slice is required. (The old
    // Rekor-only store entry is gone: a pure Rekor backend lives only in Rekor's store.)
    wayai: templateYamlWayaiBlock,
  })
  .refine((c) => Boolean(c.wayai.locales[c.defaultLocale]), {
    message: '`wayai.locales` must include the defaultLocale',
  });
export type TemplateYaml = z.infer<typeof templateYamlSchema>;

// =============================================================================
// Admin write — `POST /api/admin/templates/push`
// =============================================================================

// Hero image shipped inline (base64). Optional. RASTER only — the hero serves from
// the public R2 bucket at the backend origin, and an SVG could carry inline script.
export const templateHeroSchema = z.object({
  filename: z
    .string()
    .min(1)
    .max(200)
    .regex(/\.(png|jpe?g|webp|gif)$/i, 'hero must be a raster image (png/jpg/webp/gif)'),
  dataBase64: z.string().min(1).max(8_000_000),
});
export type TemplateHero = z.infer<typeof templateHeroSchema>;

// One locale's payload. README ships as RAW markdown; the backend renders +
// sanitizes per locale (single sanitization authority). wayaiFiles absent ⇒ this
// locale has no WayAI slice (falls back to the default locale's on read).
export const templatePushLocaleSchema = z.object({
  wayai: templateTextSchema.optional(),
  readmeMarkdown: z.string().default(''),
  wayaiFiles: templateFileMapSchema.optional(),
});
export type TemplatePushLocale = z.infer<typeof templatePushLocaleSchema>;

export const templatePushBody = z
  .object({
    slug: templateSlugSchema,
    defaultLocale: templateLocaleSchema,
    common: z.object({
      category: z.string().min(1).max(60),
      tags: templateTagsSchema,
      status: templateStatusSchema,
      order: z.number().int().min(0).max(100000).default(0),
      // The WayAI install command (a WayAI template always has a wayai side). Kept
      // optional on the wire so a malformed manifest 400s on the field, not the body.
      installCommand: z.string().min(1).max(300).optional(),
      hero: templateHeroSchema.optional(),
      // The channel→backend edge (gh #2955), locale-agnostic.
      uses: templateUsesSchema,
    }),
    locales: z.record(templateLocaleSchema, templatePushLocaleSchema),
  })
  .refine((b) => Boolean(b.locales[b.defaultLocale]), {
    message: '`locales` must include the defaultLocale',
  });
export type TemplatePushBody = z.infer<typeof templatePushBody>;

export type TemplatePushAction = 'upserted' | 'unpublished' | 'noop';

export interface TemplatePushResponse {
  slug: string;
  action: TemplatePushAction;
  filesWritten: number;
  filesDeleted: number;
}

// =============================================================================
// Consumer read — `GET /api/ci/templates` (list) + `/:slug` (detail / pull)
// Each response is resolved to a single requested locale (with fallback).
// =============================================================================
export interface TemplateIncludes {
  wayai: boolean; // has a pullable WayAI slice (always true for a published WayAI template)
}

export const templateListQuery = z.object({ locale: templateLocaleSchema.optional() });
export const templateSlugParam = z.object({ slug: templateSlugSchema });
export const templateDetailQuery = z.object({ locale: templateLocaleSchema.optional() });

export interface TemplateListItem {
  slug: string;
  category: string;
  tags: string[]; // normalized cross-cutting filter tags (website gallery)
  order: number;
  title: string; // localized card title (resolved locale's metaTitle ?? slug)
  heroUrl: string | null;
  includes: TemplateIncludes;
  uses: TemplateUsesEntry[]; // channel→backend edge (gh #2955); empty = standalone channel
  availableLocales: TemplateLocale[];
}
export interface TemplateListResponse {
  templates: TemplateListItem[];
}

// Resolved per-domain block on the wire (installCommand copied from common so the
// website keeps `t.wayai.installCommand`; the page appends `--lang` per locale).
export interface TemplateWayaiBlock {
  metaTitle: string;
  metaDescription: string;
  installCommand: string;
}

export interface TemplateDetail {
  slug: string;
  category: string;
  tags: string[]; // normalized cross-cutting filter tags (website gallery)
  order: number;
  heroUrl: string | null;
  wayai: TemplateWayaiBlock | null;
  uses: TemplateUsesEntry[]; // channel→backend edge; the page derives the cross-link via deriveRekorBackendUrl
  readmeHtml: string;
  includes: TemplateIncludes;
  files: Record<string, string>; // wayai slice file-map for the resolved locale
  availableLocales: TemplateLocale[];
  requestedLocale: TemplateLocale;
  resolvedLocale: TemplateLocale; // differs from requested when the CLI fell back
}
